// @ts-check
/** @typedef {import('../types/api').InstagramFeedItem} InstagramFeedItem */
/** @typedef {import('../types/api').InstagramPost} InstagramPost */

import { ArrowDown, ArrowUp, Check, Film, Heart, Image, LayoutGrid, List, Plus, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../context/ToastContext';
import instagramFeedService from '../services/instagramFeedService';
import postMarketingService from '../services/postMarketingService';

// Pattern: columns of 3, alternating A/B
// Column A (0,2,4...): pos 0 = WIDE top, pos 1-2 = SMALL bottom
// Column B (1,3,5...): pos 0-1 = SMALL top, pos 2 = WIDE bottom
const getGridHint = (index) => {
  const colIdx = Math.floor(index / 3);
  const posInCol = index % 3;
  const isTypeA = colIdx % 2 === 0;

  if (isTypeA) {
    return posInCol === 0
      ? { label: 'WIDE (top)', color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' }
      : { label: 'SMALL (bottom)', color: 'bg-muted text-muted-foreground' };
  } else {
    return posInCol === 2
      ? { label: 'WIDE (bottom)', color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' }
      : { label: 'SMALL (top)', color: 'bg-muted text-muted-foreground' };
  }
};

const PostTypeBadge = ({ post_type }) => {
  if (post_type === 'video' || post_type === 'reel') {
    return (
      <span className="px-1.5 py-0.5 text-[10px] rounded-md font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 flex items-center gap-0.5">
        <Film size={9} />
        {post_type === 'reel' ? 'Reel' : 'Video'}
      </span>
    );
  }
  return (
    <span className="px-1.5 py-0.5 text-[10px] rounded-md font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center gap-0.5">
      <Image size={9} />
      {post_type === 'image' ? 'Image' : 'Carousel'}
    </span>
  );
};

const InstagramFeedCurationPage = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('visible');
  /** @type {[InstagramPost[], Function]} */
  const [allPosts, setAllPosts] = useState([]);
  /** @type {[InstagramFeedItem[], Function]} */
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const feedPostIds = new Set(feedItems.map((item) => item.post_id));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [postsData, feedData] = await Promise.all([
        postMarketingService.getPosts(),
        instagramFeedService.getFeedItems(),
      ]);
      setAllPosts(Array.isArray(postsData) ? postsData : /** @type {any} */ (postsData)?.items || []);
      setFeedItems(Array.isArray(feedData) ? feedData : /** @type {any} */ (feedData)?.items || []);
    } catch (err) {
      toast.error('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async (postId) => {
    if (actionLoading) return;
    setActionLoading(postId);
    try {
      const newItem = await instagramFeedService.addFeedItem(postId);
      setFeedItems((prev) => [...prev, newItem]);
      toast.success('Post added to feed');
    } catch (err) {
      toast.error('Failed to add post: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (item) => {
    if (actionLoading) return;
    setActionLoading(item.id);
    try {
      await instagramFeedService.removeFeedItem(item.id);
      setFeedItems((prev) => prev.filter((fi) => fi.id !== item.id));
      toast.success('Post removed from feed');
    } catch (err) {
      toast.error('Failed to remove post: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMove = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= feedItems.length) return;

    const updated = [...feedItems];
    const [moved] = updated.splice(index, 1);
    updated.splice(targetIndex, 0, moved);
    setFeedItems(updated);

    try {
      const reorderPayload = updated.map((item, i) => ({
        id: item.id,
        display_order: i,
      }));
      await instagramFeedService.reorder(reorderPayload);
    } catch (err) {
      toast.error('Failed to reorder: ' + err.message);
      fetchData();
    }
  };

  const getPostById = (postId) => allPosts.find((p) => p.id === postId || p.instagram_id === postId);

  const getThumbnail = (postOrItem) => {
    if (!postOrItem) return null;
    return postOrItem.thumbnail_url || postOrItem.media_url || null;
  };

  const getCaptionPreview = (post) => {
    if (!post) return '';
    const caption = post.caption || '';
    return caption.length > 80 ? caption.slice(0, 80) + '...' : caption;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif">Instagram Feed Curation</h1>
          <p className="text-sm text-muted-foreground mt-1">Select and order posts for the customer-facing feed</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border overflow-x-auto">
        <button
          onClick={() => setActiveTab('visible')}
          className={`flex-shrink-0 whitespace-nowrap flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'visible'
              ? 'border-foreground text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
          }`}
        >
          <List size={16} />
          Currently Visible
          {feedItems.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
              {feedItems.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'all'
              ? 'border-foreground text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
          }`}
        >
          <LayoutGrid size={16} />
          All Posts
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-accent text-muted-foreground rounded-full">
            {allPosts.length}
          </span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'all' && (
        <AllPostsTab
          posts={allPosts}
          feedPostIds={feedPostIds}
          actionLoading={actionLoading}
          onAdd={handleAdd}
          getThumbnail={getThumbnail}
          getCaptionPreview={getCaptionPreview}
        />
      )}

      {activeTab === 'visible' && (
        <CurrentlyVisibleTab
          feedItems={feedItems}
          getPostById={getPostById}
          getThumbnail={getThumbnail}
          getCaptionPreview={getCaptionPreview}
          actionLoading={actionLoading}
          onMove={handleMove}
          onRemove={handleRemove}
          onSwitchTab={() => setActiveTab('all')}
        />
      )}
    </div>
  );
};

/* ========== All Posts Tab ========== */
const AllPostsTab = ({ posts, feedPostIds, actionLoading, onAdd, getThumbnail, getCaptionPreview }) => {
  if (posts.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Image size={32} className="mx-auto mb-3 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground/70">No synced posts found. Sync posts from the Posts page first.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {posts.map((post) => {
        const inFeed = feedPostIds.has(post.id) || feedPostIds.has(post.instagram_id);
        const thumb = getThumbnail(post);
        const isLoading = actionLoading === post.id;

        return (
          <div
            key={post.id}
            className={`relative bg-card border rounded-xl overflow-hidden transition-all ${
              inFeed
                ? 'border-green-300 dark:border-green-800'
                : 'border-border hover:border-muted-foreground hover:shadow-sm'
            }`}
          >
            {/* Thumbnail */}
            <div
              className="w-full bg-muted flex items-center justify-center overflow-hidden"
              style={{ height: '120px' }}
            >
              {thumb ? (
                <img src={thumb} alt="" className="w-full h-full object-cover" />
              ) : (
                <Image size={24} className="text-muted-foreground/50" />
              )}
            </div>

            {/* In Feed badge */}
            {inFeed && (
              <div className="absolute top-2 right-2">
                <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-green-500 text-white rounded-full shadow">
                  <Check size={10} />
                  In Feed
                </span>
              </div>
            )}

            {/* Loading overlay */}
            {isLoading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                <RefreshCw size={16} className="animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Info */}
            <div className="p-3 space-y-2">
              <div className="flex items-center gap-1.5">
                <PostTypeBadge post_type={post.post_type} />
                {post.like_count != null && (
                  <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground/70">
                    <Heart size={10} /> {post.like_count}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground leading-tight truncate">{getCaptionPreview(post)}</p>
              <button
                onClick={() => !inFeed && onAdd(post.id)}
                disabled={inFeed || !!actionLoading}
                className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  inFeed
                    ? 'bg-muted text-muted-foreground/70 cursor-not-allowed'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
                }`}
              >
                {inFeed ? (
                  <>
                    <Check size={12} />
                    Added
                  </>
                ) : (
                  <>
                    <Plus size={12} />
                    Add to Feed
                  </>
                )}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ========== Currently Visible Tab ========== */
const CurrentlyVisibleTab = ({
  feedItems,
  getPostById,
  getThumbnail,
  getCaptionPreview,
  actionLoading,
  onMove,
  onRemove,
  onSwitchTab,
}) => {
  return (
    <div className="space-y-6">
      {/* Section A: Ordered List */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h2 className="text-lg font-medium mb-4">
          Feed Order
          <span className="text-sm font-normal text-muted-foreground/70 ml-2">
            ({feedItems.length} {feedItems.length === 1 ? 'post' : 'posts'})
          </span>
        </h2>

        {feedItems.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground/70">
            <Plus size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No posts selected.</p>
            <button onClick={onSwitchTab} className="mt-2 text-sm text-foreground underline hover:no-underline">
              Go to &lsquo;All Posts&rsquo; tab to add posts to your feed.
            </button>
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {feedItems.map((item, index) => {
              const post = getPostById(item.post_id);
              const thumb = getThumbnail(post) || getThumbnail(item);
              const isRemoving = actionLoading === item.id;
              const hint = getGridHint(index);

              return (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-2 border border-border rounded-lg hover:bg-muted/50 group"
                >
                  {/* Position number */}
                  <div className="flex-shrink-0 w-8 text-center">
                    <span className="text-sm font-bold text-foreground">#{index + 1}</span>
                  </div>

                  {/* Thumbnail */}
                  <div
                    className="flex-shrink-0 bg-muted rounded-lg overflow-hidden flex items-center justify-center"
                    style={{ width: '60px', height: '60px' }}
                  >
                    {thumb ? (
                      <img src={thumb} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Image size={16} className="text-muted-foreground/50" />
                    )}
                  </div>

                  {/* Grid hint badge */}
                  <div className="flex-shrink-0">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md ${hint.color}`}>{hint.label}</span>
                  </div>

                  {/* Caption */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground/80 truncate">
                      {getCaptionPreview(post) || getCaptionPreview(item) || 'No caption'}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => onMove(index, -1)}
                      disabled={index === 0}
                      className="p-1 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      onClick={() => onMove(index, 1)}
                      disabled={index === feedItems.length - 1}
                      className="p-1 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ArrowDown size={14} />
                    </button>
                    <button
                      onClick={() => onRemove(item)}
                      disabled={isRemoving}
                      className="p-1 rounded-lg text-muted-foreground/70 hover:bg-red-100 dark:hover:bg-red-900/60 hover:text-red-600 dark:hover:text-red-400"
                      title="Remove from feed"
                    >
                      {isRemoving ? <RefreshCw size={14} className="animate-spin" /> : <X size={14} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Section B: Mini Preview Grid */}
      {feedItems.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h2 className="text-lg font-medium">Preview</h2>
          <p className="text-sm text-muted-foreground mb-4">How the feed will look on your website</p>

          <div
            className="overflow-x-auto overflow-y-hidden border border-border rounded-lg bg-muted/50 p-2"
            style={{ height: '240px' }}
          >
            <div className="flex h-full" style={{ gap: 0 }}>
              {(() => {
                const cols = [];
                for (let i = 0; i < feedItems.length; i += 3) {
                  cols.push(feedItems.slice(i, i + 3));
                }
                return cols.map((col, colIdx) => {
                  const isTypeA = colIdx % 2 === 0;
                  const renderThumb = (item) => {
                    const post = getPostById(item.post_id);
                    const thumb = getThumbnail(post) || getThumbnail(item);
                    return thumb ? (
                      <img src={thumb} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-accent" />
                    );
                  };
                  return (
                    <div
                      key={colIdx}
                      className="flex-shrink-0 flex flex-col"
                      style={{ width: '160px', height: '100%' }}
                    >
                      {isTypeA ? (
                        <>
                          {col[0] && (
                            <div className="overflow-hidden" style={{ height: '60%' }}>
                              {renderThumb(col[0])}
                            </div>
                          )}
                          <div className="flex" style={{ height: '40%' }}>
                            {col[1] && (
                              <div className="overflow-hidden" style={{ width: '50%' }}>
                                {renderThumb(col[1])}
                              </div>
                            )}
                            {col[2] && (
                              <div className="overflow-hidden" style={{ width: '50%' }}>
                                {renderThumb(col[2])}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex" style={{ height: '40%' }}>
                            {col[0] && (
                              <div className="overflow-hidden" style={{ width: '50%' }}>
                                {renderThumb(col[0])}
                              </div>
                            )}
                            {col[1] && (
                              <div className="overflow-hidden" style={{ width: '50%' }}>
                                {renderThumb(col[1])}
                              </div>
                            )}
                          </div>
                          {col[2] && (
                            <div className="overflow-hidden" style={{ height: '60%' }}>
                              {renderThumb(col[2])}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramFeedCurationPage;
